/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Console information architecture (redesign, Oct 2026).
// QuickPods lives in this portal; TrainPods and Inference are separate apps
// (training-portal, inference-portal) and their links leave the portal.

import React from 'react';
import {
  HomeIc, GridIc, ChipIc, TerminalIc, DriveIc, LayersIc, TrendIc, ServersIc, BoltIc, ChatIc, KeyIc, DbIc,
  FolderIc, CardIc, SlidersIc, BoxIc,
} from '../components/console/icons';
import {
  isSandboxEnabled, isManagedMLOpsEnabled, isVMManagerEnabled, isBaremetalEnabled, isReactiveNotebooksEnabled,
  isAIStudioEnabled, isMLOpsEnabled, isFinOpsEnabled, isFileStorageEnabled, isKYCEnforcementEnabled,
  isPodvirtInferenceEnabled,
} from './featureFlags';

const trim = (u: string) => u.replace(/\/+$/, '');
export const TRAINING_PORTAL_URL = trim(process.env.REACT_APP_TRAINING_PORTAL_URL || 'https://training.podstack.ai/training');
export const INFERENCE_PORTAL_URL = trim(process.env.REACT_APP_INFERENCE_PORTAL_URL || 'https://inference.podstack.ai/inference');

export interface ConsoleNavItem {
  id: string;
  name: string;
  href: string;
  icon: (p: React.SVGProps<SVGSVGElement>) => React.ReactElement;
  external?: boolean;
  end?: boolean;
  /** Extra path prefixes that should mark this item active. */
  match?: string[];
}

export interface ConsoleNavGroup {
  id: string;
  label?: string;
  hint?: string;
  items: ConsoleNavItem[];
}

export const getConsoleNav = (): ConsoleNavGroup[] => {
  const groups: ConsoleNavGroup[] = [
    {
      id: 'start',
      items: [
        { id: 'nav-dashboard', name: 'Home', href: '/', icon: HomeIc, end: true },
        { id: 'nav-services', name: 'All services', href: '/services', icon: GridIc },
      ],
    },
    {
      id: 'quickpods',
      label: 'QuickPods',
      hint: 'GPU cloud',
      items: [
        { id: 'nav-pods', name: 'GPU Pods', href: '/pods', icon: ChipIc, match: ['/templates'] },
        ...(isSandboxEnabled() ? [{ id: 'nav-sandboxes', name: 'Sandboxes', href: '/sandboxes', icon: TerminalIc }] : []),
        { id: 'nav-volumes', name: 'NFS volumes', href: '/volumes', icon: DriveIc },
        ...(isManagedMLOpsEnabled()
          ? [{ id: 'nav-managed-mlops', name: 'Managed MLOps', href: '/managed-mlops', icon: LayersIc }]
          : []),
      ],
    },
    {
      id: 'trainpods',
      label: 'TrainPods',
      hint: 'Training studio',
      items: [
        { id: 'nav-train-gpus', name: 'GPU catalog', href: `${TRAINING_PORTAL_URL}/console/gpus`, icon: TrendIc, external: true },
        { id: 'nav-train-instances', name: 'Instances', href: `${TRAINING_PORTAL_URL}/console/instances`, icon: ServersIc, external: true },
      ],
    },
    {
      id: 'inference',
      label: 'Inference',
      hint: 'Serverless models',
      items: [
        { id: 'nav-inf-models', name: 'Models', href: `${INFERENCE_PORTAL_URL}/console/models`, icon: BoltIc, external: true },
        { id: 'nav-inf-playground', name: 'Playground', href: `${INFERENCE_PORTAL_URL}/console/playground`, icon: ChatIc, external: true },
        { id: 'nav-inf-keys', name: 'API keys', href: `${INFERENCE_PORTAL_URL}/console/keys`, icon: KeyIc, external: true },
      ],
    },
    {
      id: 'storage',
      label: 'Storage',
      items: [
        { id: 'nav-drive', name: 'Object storage', href: '/drive', icon: DbIc },
        ...(isFileStorageEnabled() ? [{ id: 'nav-storage', name: 'File storage', href: '/storage', icon: DbIc }] : []),
      ],
    },
    {
      id: 'account',
      label: 'Account',
      items: [
        { id: 'nav-projects', name: 'Projects', href: '/projects', icon: FolderIc, match: ['/audit-logs', '/organization'] },
        { id: 'nav-billing', name: 'Billing', href: '/billing', icon: CardIc, match: ['/wallet', '/invoices'] },
        { id: 'nav-settings', name: 'Settings', href: '/settings', icon: SlidersIc, match: ['/ssh-keys'] },
      ],
    },
  ];

  // Flag-gated surfaces that aren't part of the core IA keep a home under "More".
  const more: ConsoleNavItem[] = [
    ...(isVMManagerEnabled() ? [{ id: 'nav-vms', name: 'Virtual machines', href: '/vms', icon: ServersIc }] : []),
    ...(isBaremetalEnabled() ? [{ id: 'nav-baremetal', name: 'Baremetal', href: '/baremetal/marketplace', icon: ServersIc, match: ['/baremetal'] }] : []),
    ...(isReactiveNotebooksEnabled() ? [{ id: 'nav-notebooks', name: 'Notebooks', href: '/notebooks', icon: TerminalIc }] : []),
    ...(isMLOpsEnabled() ? [{ id: 'nav-registry', name: 'MLOps registry', href: '/registry/models', icon: LayersIc, match: ['/registry'] }] : []),
    ...(isPodvirtInferenceEnabled() ? [{ id: 'nav-serverless', name: 'Serverless GPU', href: '/inference/serverless', icon: BoltIc, match: ['/inference'] }] : []),
    ...(isAIStudioEnabled() ? [{ id: 'nav-ai-studio', name: 'AI Studio', href: '/ai-studio/models', icon: BoxIc, match: ['/ai-studio'] }] : []),
    ...(isFinOpsEnabled() ? [{ id: 'nav-finops', name: 'FinOps', href: '/finops', icon: TrendIc }] : []),
    ...(isKYCEnforcementEnabled() ? [{ id: 'nav-kyc', name: 'KYC', href: '/kyc', icon: FolderIc }] : []),
  ];
  if (more.length) groups.push({ id: 'more', label: 'More', items: more });

  return groups;
};
